/* eslint-disable @typescript-eslint/no-empty-function */
import React, {useContext, useEffect, useState} from "react";
import {RevenueModelType, useGetAcquisitionPropertyLazyQuery} from "../../__generated__/generated_types";
import {AuthContext, AuthUser} from "../AuthContext";
import {GuestAuthenticationContext} from "../guest-auth-context/GuestAuthContext";
import {asBudgetProperty, MinimalProperty, Property} from "./logic";
import mixpanel from "mixpanel-browser";
import {MixPanelContext} from "../../mixpanel/MixPanelWrapper";
import {Whoops} from "../../components/whoops/Whoops";
import {Button} from "@zendeskgarden/react-buttons";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import {ViziblyTheme} from "../../styles/zendesk-garden/ViziblyZDGTheme";
import {checkAuthorization} from "../../authorization/AuthorizationCheckers";
import {Authorizations} from "../../authorization/Authorizations";
import {UserManagerRoute} from "../../routes/UserManagerRoute";

const PROPERTY_ID_KEY = 'vizibly-property-id';
const ACQUISITION_PROPERTY_ID_KEY = 'vizibly-acquisition-id-property';

export type {Property} from "./logic";

export type UnitType = {
    id: string,
    type: string,
}

export interface PropertiesState {
    properties: Property[] | undefined;
    currentProperty: Property | undefined;
    allAcquisitionProperties: Property[] | undefined;
    allNormalProperties: Property[] | undefined;
    propertyId: string;
    chooseProperty: (propertyId: string) => void;
    activateAcquisitionProperty: (property: Property, similar: Property[], all: Property[]) => void;
    deactivateAcquisitionProperty: () => void;
    refreshProperties: () => void;
    loaded: boolean;
    loading: boolean;

}

const PropertiesStateContext = React.createContext<PropertiesState | undefined>(undefined);

function usePropertiesState() {
    const {user, reloadUserWithProperties} = useContext(AuthContext);
    const {initialized: mixpanelInitialized} = useContext(MixPanelContext);
    const [properties, setProperties] = useState<Property[]>(buildPropertiesFromUser(user));
    const [currentProperty, setCurrentProperty] = useState<Property | undefined>(undefined);
    const [currentPropertyId, setCurrentPropertyId] = useState<string | undefined>(() => localStorage.getItem(PROPERTY_ID_KEY) || undefined);
    const [activeAcquisitionProperty, setActiveAcquisitionProperty] = useState<Property | undefined>(undefined);
    const [similarAcquisitionProperties, setSimilarAcquisitionProperties] = useState<Property[]>([]);
    const [allAcquisitionProperties, setAllAcquisitionProperties] = useState<Property[] | undefined>([]);
    const [activeAcquisitionPropertyId, setActiveAcquisitionPropertyId] = useState<string | undefined>(() => localStorage.getItem(ACQUISITION_PROPERTY_ID_KEY) || undefined);
    const [isDataLoaded, setDataLoaded] = useState<boolean>();

    const [getAcquisitionProperty, {data: dataAcquisitionProperty, loading: loadingAcquisitionProperty}] = useGetAcquisitionPropertyLazyQuery({
        fetchPolicy: "no-cache"
    });

    function buildPropertiesFromUser(user: AuthUser | null): Property[] {
        if(!user) {
            return [];
        }
        return user.properties.map(p => ({
            ...p,
            budgetYear: p.reforecastYear + 1,
            name: p.displayName ? p.displayName : p.name,
            systemName: p.name,
            isAcquisitionProperty: false
        })).sortBy("name") ?? [];
    }

    function choosePropertyInternal(propertyId: string, properties: Property[]) {
        if(activeAcquisitionPropertyId || activeAcquisitionProperty) {
            return;
        }
        let chosenProperty = properties.find(property => property.id === propertyId);
        if(!chosenProperty && properties.length > 0) {
            chosenProperty = properties[0];
        }
        const chosenPropertyId = chosenProperty?.id ?? 'not found';
        setCurrentProperty(chosenProperty);
        setCurrentPropertyId(chosenPropertyId);
        localStorage.setItem(PROPERTY_ID_KEY, chosenPropertyId);

        // make sure mixpanel is ready, we have a chosen property, and we have a user that is able to be tied to the events
        if (mixpanelInitialized && chosenProperty?.id && user?.email) {
            // sets three mixpanel super properties to record property info on the auto page view events
            mixpanel.register({
                "propertyId": chosenProperty.id,
                "propertyName": chosenProperty.name,
                "propertyType": chosenProperty.propertyType,
            });
            // record when the property switch happened with a custom event, for calculating session length
            // per-property within mixpanel
            mixpanel.track("Property Switch", {
                "propertyId": chosenProperty.id,
                "propertyName": chosenProperty.name,
                "propertyType": chosenProperty.propertyType,
            });
        }
    }

    function chooseProperty(propertyId: string): void {
        if (properties) {
            choosePropertyInternal(propertyId, properties);
        }
    }

    function activateAcquisitionProperty(property: Property, similar: Property[], all: Property[]) {
        setActiveAcquisitionProperty(property);
        setSimilarAcquisitionProperties(similar);
        setAllAcquisitionProperties(all);
        localStorage.setItem(ACQUISITION_PROPERTY_ID_KEY, property.id);
    }

    function deactivateAcquisitionProperty() {
        setActiveAcquisitionProperty(undefined);
        setActiveAcquisitionPropertyId(undefined);
        setSimilarAcquisitionProperties([]);
        setAllAcquisitionProperties([]);
        localStorage.removeItem(ACQUISITION_PROPERTY_ID_KEY);
    }

    useEffect(
        () => {
            const hasUser = user !== null && user !== undefined;
            if (hasUser && !activeAcquisitionPropertyId) {
                if(currentPropertyId && properties.map(p => p.id).includes(currentPropertyId)) {
                    choosePropertyInternal(currentPropertyId, properties);
                } else if(properties.length > 0 && properties[0]) {
                    choosePropertyInternal(properties[0].id, properties);
                }
                setDataLoaded(properties.length > 0);
                return;
            } else if(hasUser && !activeAcquisitionProperty && activeAcquisitionPropertyId) {
                getAcquisitionProperty({
                    variables: {
                        propertyId: activeAcquisitionPropertyId
                    }
                });
            } else if (activeAcquisitionProperty) {
                setDataLoaded(true);
                return;
            }
            setDataLoaded(false);
        },
        [user, properties, activeAcquisitionProperty, activeAcquisitionPropertyId]
    );

    useEffect(() => {
        if(loadingAcquisitionProperty) {
            return;
        }

        if(dataAcquisitionProperty?.getAcquisitionProperty) {
            setActiveAcquisitionProperty(asBudgetProperty(dataAcquisitionProperty.getAcquisitionProperty));
            setSimilarAcquisitionProperties(dataAcquisitionProperty.getAcquisitionProperty.similarAcquisitionProperties.map(p => asBudgetProperty(p)));
            if(dataAcquisitionProperty.listAcquisitionProperties) {
                setAllAcquisitionProperties(dataAcquisitionProperty.listAcquisitionProperties.map(p => asBudgetProperty(p)));
            } else {
                setAllAcquisitionProperties([]);
            }
            setDataLoaded(true);
        } else if(dataAcquisitionProperty !== undefined) {
            deactivateAcquisitionProperty();
        }
    }, [dataAcquisitionProperty, loadingAcquisitionProperty]);

    useEffect(
        () => {
            setProperties(buildPropertiesFromUser(user));
        },
        [user]
    );

    function refreshProperties(): void {
        if(activeAcquisitionProperty) {
            getAcquisitionProperty({
                variables: {
                    propertyId: activeAcquisitionProperty.id
                }
            });
        } else {
            reloadUserWithProperties();
        }
    }

    return {
        properties: activeAcquisitionProperty ? [activeAcquisitionProperty, ...similarAcquisitionProperties] : properties,
        currentProperty: activeAcquisitionProperty ? activeAcquisitionProperty : currentProperty,
        chooseProperty: activeAcquisitionProperty ? () => {/* DO NOTHING  */} : chooseProperty,
        allAcquisitionProperties: activeAcquisitionProperty ? allAcquisitionProperties : [],
        allNormalProperties: properties,
        activateAcquisitionProperty,
        deactivateAcquisitionProperty,
        refreshProperties,
        loaded: isDataLoaded,
        loading: user === null || user === undefined
    };
}

export const PropertiesProvider: React.FunctionComponent = ({children}) => {
    const {user, signOut} = useContext(AuthContext);
    const {
        properties,
        currentProperty,
        chooseProperty,
        allAcquisitionProperties,
        allNormalProperties,
        activateAcquisitionProperty,
        deactivateAcquisitionProperty,
        refreshProperties,
        loaded,
        loading
    } = usePropertiesState();

    const value: PropertiesState = {
        properties,
        currentProperty,
        chooseProperty,
        allAcquisitionProperties,
        allNormalProperties,
        activateAcquisitionProperty,
        deactivateAcquisitionProperty,
        refreshProperties,
        propertyId: currentProperty?.id ?? "",
        loaded: loaded ?? false,
        loading: loading
    };

    if(!currentProperty) {
        if(!properties || properties.length === 0) {
            if(checkAuthorization(user, Authorizations.ManageUsers)) {
                return (
                    <PropertiesStateContext.Provider value={value}>
                        <UserManagerRoute />
                    </PropertiesStateContext.Provider>
                );
            }
            return <Whoops
                whoopsMessage="No Properties!"
                message="It looks like you weren't assigned any properties. Please reach out to your Vizibly administrator."
                button={<ThemeProvider theme={ViziblyTheme}><Button isPrimary onClick={() => signOut?.()}>Logout</Button></ThemeProvider>}
            />;
        }

        // Quite a lot of the app depends on the currentProperty or currentPropertyId being
        // available no matter what when calling useProperties. The best way to ensure this
        // at this point is to prevent rendering anything until the currentProperty is set.
        return <></>;
    }

    return (
        <PropertiesStateContext.Provider value={value}>
            {children}
        </PropertiesStateContext.Provider>
    );
};

export function useProperties(): PropertiesState {
    const context = React.useContext(PropertiesStateContext);
    const guestAuthContext = React.useContext(GuestAuthenticationContext);

    if (context) {
        return context;
    }

    if (guestAuthContext) {
        return {
            get properties(): Property[] | undefined { throw new Error("Not allowed for guest users"); },
            get currentProperty() { return guestAuthContext.user?.property; },
            get propertyId() { return guestAuthContext.user?.property?.id ?? ""; },
            chooseProperty: (_: string) => { throw new Error("Not allowed for guest users"); },
            allAcquisitionProperties: undefined,
            allNormalProperties: undefined,
            activateAcquisitionProperty: () => { throw new Error("Not allowed for guest users"); },
            deactivateAcquisitionProperty: () => { throw new Error("Not allowed for guest users"); },
            refreshProperties: () => { throw new Error("Not allowed for guest users"); },
            get loaded(): boolean { throw new Error("Not allowed for guest users"); },
            get loading(): boolean { throw new Error("Not allowed for guest users"); }
        };
    }

    throw new Error('useProperties must be used within a PropertiesProvider or GuestAuthenticationContextProvider');
}

/**
 * Simple function that given a property will tell you if it has the given revenue model.
 * If the property is undefined or null, because it isn't available yet for some reason, this
 * will default to saying the property has the original model.
 *
 * @param property Property object.
 * @param revenueModel The revenue model type to check if the property has. If mulitple, the check is an OR.
 */
export function propertyHasRevenueModel(property: Property | undefined | null, revenueModel: RevenueModelType | RevenueModelType[]): boolean {
    if(property === undefined || property === null) {
        if(Array.isArray(revenueModel)) {
            return revenueModel.includes(RevenueModelType.Original);
        }
        return revenueModel === RevenueModelType.Original;
    }

    if(Array.isArray(revenueModel)) {
        return revenueModel.includes(property.revenueModel);
    }
    return property.revenueModel === revenueModel;
}

export function getBudgetYearForProperty(property: MinimalProperty | undefined, budgetYear: number): number {
    if (property?.isAcquisitionProperty) {
        return property.budgetYear;
    }

    return budgetYear;
}